import { Flex } from '@brightdrop/bd-ui';
import { useTranslations } from '@brightdrop/localization-client';
import { cx } from '@emotion/css';
import {
  Avatar,
  Badge,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { AlertRecord, SingleAlertRecord } from '~/api/generated-api';
import PinIcon from '~/assets/icons/new/pin.svg?react';
import UnPinIcon from '~/assets/icons/new/unPin.svg?react';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import useToasts from '~/common/components/toasts/useToasts';
import { useBuildAssetUrl } from '~/common/hooks/useBuildAssetUrl';
import { HEAP_EVENTS } from '~/common/models/heap.model';
import { dispatchHeapEvent } from '~/common/utils/analytics/heap.utils';
import { NOTIFICATION_SETTINGS_MESSAGES } from '~/routes/settings/settings.messages';
import { getNotificationIcon } from '~/routes/settings/settings.utils';
import { selectActingProfile } from '~/store/profile/profileSlice.selectors';
import { eventToMessage } from '~/utils/alerts/alerts.utils';
import { usePinnedAlerts } from '../../../providers/PinnedAlertsProvider';
import {
  type NotificationOption,
  useSettingsNotificationOption,
} from '../../../utils/notification.utils';
import NotificationActionsMenu from '../../Buttons/NotificationActionsMenu';
import NotificationDateTime from '../../NotificationDateTime';
import { useStyles } from '../NotificationCard.styles';
import NotificationDismissButton from './NotificationDismissButton';
import NotificationViewButton from './NotificationViewButton';

type Props = {
  item: SingleAlertRecord;
  context?: 'popover' | 'page';
  inPinnedSection?: boolean;
  onDismissNotification?: (alert: AlertRecord) => void;
  onClose?: () => void;
};

const NotificationCard = memo<Props>(function NotificationCard({
  item,
  context = 'page',
  inPinnedSection = false,
  onDismissNotification,
  onClose,
}) {
  const theme = useTheme();
  const { classes } = useStyles();
  // Subscribe to i18n so imperative `translateText(...)` updates after locale changes.
  useTranslation(['settings', 'common']);
  const { translations } = useTranslations(NOTIFICATION_SETTINGS_MESSAGES);
  const { showErrorToast } = useToasts();

  const { buildAssetUrl } = useBuildAssetUrl();
  const actingProfile = useSelector(selectActingProfile);
  const locale = actingProfile?.userPreferences?.locale;
  const timeZone = actingProfile?.userPreferences?.timeZone;

  const {
    addPinnedAlert,
    removePinnedAlert,
    isPinned,
    isPinnedAlertsModified,
  } = usePinnedAlerts();

  const event = item?.event?.event ?? item?.lastGroupEvent?.event.event;
  const category =
    item?.event?.category ?? item?.lastGroupEvent?.event?.category;

  const handleNotificationLinkClick = useCallback(() => {
    dispatchHeapEvent(HEAP_EVENTS.NOTIFICATION_LINK_CLICKED, {
      location: context,
      eventCategory: category,
      eventType: event,
    });
    onClose?.();
  }, [context, category, event, onClose]);

  const handleDismissError = useCallback(
    (error: Error) => {
      console.error('Failed to dismiss alert:', error);
      showErrorToast(translateText('common:errors.anErrorOccurred'));
    },
    [showErrorToast]
  );

  const handleDismissSuccess = useCallback(
    (alert: AlertRecord) => {
      onDismissNotification?.(alert);
    },
    [onDismissNotification]
  );

  const isAlertPinned = useMemo(() => {
    /* Pinning does not matter within the popover context */
    if (context === 'popover') return false;

    /* If the card is in the pinned section, it is always pinned */
    if (inPinnedSection) return true;

    /* Otherwise, check if the pinned list has been modified by the user and use api value or local state accordingly */
    return isPinnedAlertsModified ? isPinned(item) : item.pinned;
  }, [item, context, inPinnedSection, isPinned, isPinnedAlertsModified]);

  /* Define notification action options */
  const notificationOptions: NotificationOption[] = [
    {
      text: isAlertPinned
        ? translateText('settings:notificationList.actionMenu.unPin')
        : translateText('settings:notificationList.actionMenu.pin'),

      onClick: (e, handleClose) => {
        e.stopPropagation();
        if (isAlertPinned) {
          removePinnedAlert(item);
        } else {
          addPinnedAlert(item);
        }
        handleClose();
      },
      icon: isAlertPinned ? <UnPinIcon /> : <PinIcon />,
      id: 'pin',
    },
    useSettingsNotificationOption(item),
  ];

  return (
    <>
      <ListItem
        className={cx(
          classes[
            'rt--notifications--cmp--list--list-item--notification-item--list-item'
          ],
          context === 'popover' && classes.pagePopoverContext
        )}
        data-testid={`notification-item--${item.id}`}
        sx={{ width: context === 'popover' ? '468px' : '100%' }}
        secondaryAction={
          context === 'popover' ? (
            <NotificationDismissButton
              alert={item}
              onError={handleDismissError}
              onSuccess={handleDismissSuccess}
            />
          ) : (
            <NotificationActionsMenu options={notificationOptions} />
          )
        }
      >
        <Flex center>
          <ListItemAvatar>
            <Avatar
              className={`${classes.icon} ${item.read ? '' : classes.unRead}`}
            >
              <Box
                component="img"
                src={getNotificationIcon(event, category)}
                alt={
                  event ||
                  translateText('settings:notificationList.genericNotification')
                }
                sx={{ width: 24, height: 24 }}
              />
            </Avatar>
            {!item.read && (
              <Badge
                data-testid="unread-notification-icon"
                aria-label={translateText(
                  'settings:notificationList.unreadNotification'
                )}
                overlap="circular"
                variant="dot"
                className={classes.badge}
              />
            )}
          </ListItemAvatar>
          <Box sx={{ flex: 1 }}>
            <ListItemText
              data-testid="notification-list-item-text"
              primary={
                <Typography
                  variant="body_regular"
                  className={classes.notificationText}
                >
                  {eventToMessage(
                    item,
                    translations,
                    handleNotificationLinkClick,
                    buildAssetUrl,
                    timeZone,
                    locale
                  )}
                </Typography>
              }
              secondary={
                <Typography
                  variant="label_regular"
                  color={theme.new.color.textSecondary}
                >
                  <NotificationDateTime value={item.lastEventDateTime} />
                </Typography>
              }
            />
            <NotificationViewButton
              item={item}
              onClick={handleNotificationLinkClick}
            />
          </Box>
        </Flex>
      </ListItem>
    </>
  );
});

export default NotificationCard;
